import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { MessageCircle, SquarePen } from "lucide-react";
import { useAuth } from "../scripts/AuthContext";
import { useSocialDmConversations } from "../hooks/useSocialDmConversations";
import { getSocialDmConversation } from "@/lib/socialDmApi";
import SocialDmConversationSections from "./dm/SocialDmConversationSections";
import SocialDmThread from "./dm/SocialDmThread";
import SocialNewDmChatDialog from "./dm/SocialNewDmChatDialog";
import { AddToHomeScreenBanner } from "./AddToHomeScreenOffer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import "@/styles/social-messages.css";
import { useDmAlerts } from "../scripts/DmAlertContext";

export default function SocialMessagesPage() {
  const { conversationId: conversationIdParam } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { suggestDesktopNotifications } = useDmAlerts();
  const conversationId = conversationIdParam
    ? Number(conversationIdParam)
    : null;

  useEffect(() => {
    suggestDesktopNotifications();
  }, [suggestDesktopNotifications]);

  const [newChatOpen, setNewChatOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [resolvedConversation, setResolvedConversation] = useState(null);

  const { conversations, loading, refresh, setConversations } =
    useSocialDmConversations({ unreadOnly });

  const currentUserId = useMemo(() => {
    try {
      return accessToken ? jwtDecode(accessToken)?.sub : null;
    } catch {
      return null;
    }
  }, [accessToken]);

  const selectedFromList = useMemo(() => {
    if (!conversationId) return null;
    return (
      conversations.find(
        (c) => Number(c.conversation_id) === conversationId
      ) || null
    );
  }, [conversations, conversationId]);

  useEffect(() => {
    setResolvedConversation(null);
    if (!accessToken || !conversationId || selectedFromList) return;
    let cancelled = false;
    (async () => {
      try {
        const conv = await getSocialDmConversation(
          accessToken,
          conversationId
        );
        if (!cancelled) setResolvedConversation(conv);
      } catch (err) {
        if (!cancelled) console.error("Failed to resolve conversation:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, conversationId, selectedFromList]);

  const selected = selectedFromList || resolvedConversation;

  const handleSelect = (c) => {
    navigate(`/messages/${c.conversation_id}`);
  };

  const handleNewChatCreated = async (conv) => {
    await refresh();
    navigate(`/messages/${conv.conversation_id}`);
  };

  return (
    <div
      data-social-messages
      className="flex h-full min-h-0 min-w-0 flex-1 overflow-x-hidden"
    >
      <aside
        className={cn(
          "sm-panel shrink-0 border-r flex-col backdrop-blur-sm min-w-0",
          conversationId
            ? "hidden md:flex md:w-72 lg:w-80"
            : "flex w-full md:w-72 lg:w-80"
        )}
      >
        <div className="px-4 py-4 space-y-3 border-b border-[hsl(var(--sm-divider))]">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h1 className="font-semibold text-lg tracking-tight">Messages</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Chat with people you follow
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              className="sm-send shrink-0 h-10 min-w-10 rounded-full px-3 border-0 md:h-8"
              onClick={() => setNewChatOpen(true)}
            >
              <SquarePen className="h-3.5 w-3.5 mr-1.5" />
              New
            </Button>
          </div>
          <button
            type="button"
            className="sm-chip h-10 min-h-10 text-xs px-3 md:h-7 md:min-h-0"
            data-active={unreadOnly ? "true" : "false"}
            onClick={() => setUnreadOnly((v) => !v)}
          >
            Unread
          </button>
          {!conversationId ? <AddToHomeScreenBanner /> : null}
        </div>
        <div className="flex-1 min-h-0 flex flex-col">
          <SocialDmConversationSections
            conversations={conversations}
            loading={loading}
            selectedId={conversationId}
            onSelect={handleSelect}
            currentUserId={currentUserId}
          />
        </div>
      </aside>
      <main
        className={cn(
          "flex-1 min-w-0 min-h-0 flex-col relative overflow-x-hidden",
          conversationId ? "flex" : "hidden md:flex"
        )}
      >
        {conversationId ? (
          <SocialDmThread
            conversationId={conversationId}
            conversation={selected}
            onBack={() => navigate("/messages")}
            onConversationChange={(next) => {
              if (next?.conversation_id) {
                const cid = Number(next.conversation_id);
                setConversations((prev) =>
                  prev.map((c) =>
                    Number(c.conversation_id) === cid ? { ...c, ...next } : c
                  )
                );
                setResolvedConversation((prev) =>
                  prev && Number(prev.conversation_id) === cid
                    ? { ...prev, ...next }
                    : prev
                );
              }
              refresh();
            }}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--sm-accent)/0.15)] text-[hsl(var(--sm-accent))]">
              <MessageCircle className="h-7 w-7" />
            </div>
            <div className="space-y-1 max-w-xs">
              <p className="font-medium text-foreground">Start a conversation</p>
              <p className="text-sm text-muted-foreground">
                Pick someone from the list or say hi to a mutual follow.
              </p>
            </div>
            <Button
              type="button"
              className="sm-send rounded-full px-5 border-0"
              onClick={() => setNewChatOpen(true)}
            >
              <SquarePen className="h-4 w-4 mr-2" />
              New message
            </Button>
          </div>
        )}
      </main>

      <SocialNewDmChatDialog
        open={newChatOpen}
        onOpenChange={setNewChatOpen}
        accessToken={accessToken}
        currentUserId={currentUserId}
        onCreated={handleNewChatCreated}
      />
    </div>
  );
}
